// Standardized query key factory for TanStack Query
// Ensures consistent cache key structure across all features

export const createQueryKeys = <T extends string>(feature: T) => {
  // Cache the base key to avoid creating new arrays on every call
  const all = [feature] as const;

  return {
    all,
    lists: () => [...all, "list"] as const,
    list: (filters?: object) => [...all, "list", filters ?? {}] as const,
    details: () => [...all, "detail"] as const,
    detail: (id: string) => [...all, "detail", id] as const,
    infinite: () => [...all, "infinite"] as const,
    infiniteList: (filters?: object) => [...all, "infinite", filters ?? {}] as const,
  };
};

// Pre-defined query keys for each feature
export const authKeys = createQueryKeys("auth");
export const openingKeys = createQueryKeys("openings");
export const applicationKeys = createQueryKeys("applications");
export const profileKeys = createQueryKeys("profiles");
export const enterpriseKeys = createQueryKeys("enterprises");
export const userKeys = createQueryKeys("users");
export const talentKeys = createQueryKeys("talent");
export const hrKeys = createQueryKeys("hr");
export const adminKeys = createQueryKeys("admin");
export const watchlistKeys = createQueryKeys("watchlist");
export const interestKeys = createQueryKeys("interests");
export const calendarKeys = createQueryKeys("calendar");
export const chatKeys = createQueryKeys("chat");
export const notificationKeys = createQueryKeys("notifications");
export const successStoryKeys = createQueryKeys("success-stories");

// Custom query keys that don't fit the standard pattern
export const customKeys = {
  auth: {
    ...authKeys,
    currentUser: () => [...authKeys.all, "currentUser"] as const,
    session: () => [...authKeys.all, "session"] as const,
  },
  dashboard: {
    student: () => ["dashboard", "student"] as const,
    hr: () => ["dashboard", "hr"] as const,
    enterprise: (id: string) => ["dashboard", "enterprise", id] as const,
    admin: () => ["dashboard", "admin"] as const,
  },
  admin: {
    ...adminKeys,
    dashboard: () => [...adminKeys.all, "dashboard"] as const,
    systemHealth: () => [...adminKeys.all, "system-health"] as const,
    // Users
    users: (filters?: Record<string, unknown>) =>
      [...adminKeys.all, "users", filters ?? {}] as const,
    user: (id: string) => [...adminKeys.all, "users", id] as const,
    // Roles & Permissions
    roles: () => [...adminKeys.all, "roles"] as const,
    role: (id: string) => [...adminKeys.all, "roles", id] as const,
    roleUsers: (roleName: string) => [...adminKeys.all, "roles", roleName, "users"] as const,
    permissions: () => [...adminKeys.all, "permissions"] as const,
    // Feature Flags
    featureFlags: (filters?: Record<string, unknown>) =>
      [...adminKeys.all, "feature-flags", filters ?? {}] as const,
    featureFlag: (id: string) => [...adminKeys.all, "feature-flags", id] as const,
    // System Config
    systemConfig: () => [...adminKeys.all, "system-config"] as const,
  },
  stats: {
    applications: (filters?: object) => ["stats", "applications", filters ?? {}] as const,
    openings: (filters?: object) => ["stats", "openings", filters ?? {}] as const,
  },
  applications: {
    ...applicationKeys,
    byStage: () => [...applicationKeys.all, "by-stage"] as const,
    stats: () => [...applicationKeys.all, "stats"] as const,
    // Student application queries
    my: (filters?: object) => [...applicationKeys.all, "my", filters ?? {}] as const,
    myDetail: (id: string) => [...applicationKeys.all, "my", "detail", id] as const,
    myStats: () => [...applicationKeys.all, "my", "stats"] as const,
    history: (id: string) => [...applicationKeys.all, "history", id] as const,
    // HR application queries
    pendingReview: (filters?: object) =>
      [...applicationKeys.all, "pending-review", filters ?? {}] as const,
    hr: (filters?: object) => [...applicationKeys.all, "hr", filters ?? {}] as const,
    hrDetail: (id: string) => [...applicationKeys.all, "hr", "detail", id] as const,
    hrStats: () => [...applicationKeys.all, "hr", "stats"] as const,
    // Enterprise application queries
    enterprise: (filters?: object) =>
      [...applicationKeys.all, "enterprise", filters ?? {}] as const,
    forwarded: (openingId: string, filters?: object) =>
      [...applicationKeys.all, "forwarded", openingId, filters ?? {}] as const,
    enterpriseDetail: (id: string) => [...applicationKeys.all, "enterprise", "detail", id] as const,
    enterpriseSummary: (enterpriseId: string) =>
      [...applicationKeys.all, "enterprise", "summary", enterpriseId] as const,
  },
  openings: {
    ...openingKeys,
    // Public opening queries (no auth)
    publicList: (filters?: object) =>
      [...openingKeys.all, "public", "list", filters ?? {}] as const,
    publicDetail: (id: string) => [...openingKeys.all, "public", "detail", id] as const,
    // Student opening queries
    studentBrowse: (filters?: object) =>
      [...openingKeys.all, "student", "browse", filters ?? {}] as const,
    studentDetail: (id: string) => [...openingKeys.all, "student", "detail", id] as const,
    // Enterprise job queries
    enterpriseList: (filters?: object) =>
      [...openingKeys.all, "enterprise", "list", filters ?? {}] as const,
    enterpriseDetail: (jobId: string) =>
      [...openingKeys.all, "enterprise", "detail", jobId] as const,
    // HR job queries
    hrList: (filters?: object) => [...openingKeys.all, "hr", "list", filters ?? {}] as const,
    hrDetail: (jobId: string) => [...openingKeys.all, "hr", "detail", jobId] as const,
  },
  hr: {
    ...hrKeys,
    dashboardStats: () => [...hrKeys.all, "dashboard", "stats"] as const,
    pipelineStats: () => [...hrKeys.all, "pipeline", "stats"] as const,
    pendingApprovals: () => [...hrKeys.all, "pending-approvals"] as const,
    recentActivity: () => [...hrKeys.all, "activity"] as const,
    upcomingInterviews: () => [...hrKeys.all, "interviews", "upcoming"] as const,
    enterprises: (filters?: object) => [...hrKeys.all, "enterprises", filters ?? {}] as const,
    enterprise: (id: string) => [...hrKeys.all, "enterprises", id] as const,
    students: (filters?: object) => [...hrKeys.all, "students", filters ?? {}] as const,
    studentStats: () => [...hrKeys.all, "students", "stats"] as const,
    student: (id: string) => [...hrKeys.all, "students", id] as const,
    applications: (filters?: object) => [...hrKeys.all, "applications", filters ?? {}] as const,
    application: (id: string) => [...hrKeys.all, "applications", id] as const,
    auditLog: (filters?: object) => [...hrKeys.all, "audit-log", filters ?? {}] as const,
  },
  calendar: {
    ...calendarKeys,
    // Window-scoped event lists; window = { from, to } so month navigation
    // produces distinct cache entries per visible grid range
    my: (window?: object) => [...calendarKeys.all, "my", window ?? {}] as const,
    handled: (window?: object) => [...calendarKeys.all, "handled", window ?? {}] as const,
    event: (id: string) => [...calendarKeys.all, "detail", id] as const,
  },
  chat: {
    ...chatKeys,
    threads: () => [...chatKeys.all, "threads"] as const,
    thread: (id: string) => [...chatKeys.all, "thread", id] as const,
    // Base key for the infinite messages query of a thread
    messages: (threadId: string) => [...chatKeys.all, "messages", threadId] as const,
    // Recipient pools for the HR new-message dialog
    recipients: (kind: string) => [...chatKeys.all, "recipients", kind] as const,
  },
  notifications: {
    ...notificationKeys,
    // list(filters) and detail(id) come from the base factory spread above
    // Poll-driven badge count (no realtime push for notifications)
    unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
    // Per-user notification preferences (auto-created on first read)
    settings: () => [...notificationKeys.all, "settings"] as const,
    // Event types relevant to the current user's role(s) — settings filtering.
    // Sibling of settings() (NOT nested under it) so invalidating settings()
    // never clobbers this session-stable query.
    relevantEvents: () => [...notificationKeys.all, "relevant-events"] as const,
  },
  interests: {
    ...interestKeys,
    // Enterprise interest queries
    enterprise: (filters?: object) => [...interestKeys.all, "enterprise", filters ?? {}] as const,
    bookmarkCheck: (studentId: string) =>
      [...interestKeys.all, "bookmark", "check", studentId] as const,
    // Student interest queries
    student: (filters?: object) => [...interestKeys.all, "student", filters ?? {}] as const,
    studentCount: () => [...interestKeys.all, "student", "count"] as const,
  },
  successStories: {
    ...successStoryKeys,
    // Public surface (PUBLISHED only; anon gated by success_stories_public flag)
    publicList: (filters?: object) =>
      [...successStoryKeys.all, "public", "list", filters ?? {}] as const,
    publicDetail: (id: string) => [...successStoryKeys.all, "public", "detail", id] as const,
    // Admin curation surface (all statuses)
    adminList: (filters?: object) =>
      [...successStoryKeys.all, "admin", "list", filters ?? {}] as const,
  },
};
